import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Search, Eye, FileText, Trash2 } from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentClaims() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState(null);

  useEffect(() => {
    fetchClaims();
  }, [filter]);

  const fetchClaims = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await axios.get(`${API}/agent/claims${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(response.data);
    } catch (error) {
      toast.error("Failed to load claims");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      under_review: "bg-purple-100 text-purple-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      paid: "bg-emerald-100 text-emerald-800",
      closed: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const handleDeleteClick = (claim) => {
    setClaimToDelete(claim);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!claimToDelete) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/claims/${claimToDelete.claim_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Claim deleted successfully");
      setDeleteDialogOpen(false);
      setClaimToDelete(null);
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete claim");
    }
  };

  const filteredClaims = claims.filter(claim => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      claim.claim_id.toLowerCase().includes(searchLower) ||
      claim.user?.email?.toLowerCase().includes(searchLower) ||
      claim.user?.full_name?.toLowerCase().includes(searchLower) ||
      claim.last_employer?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <AgentLayout title="Claims">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Claims Management
        </h1>
        <p className="text-muted-foreground">Review and process insurance claims</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search claims..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="claim-search-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="claim-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Claims</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Claims Table */}
      <Card className="card-organic overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredClaims.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Claim ID</th>
                    <th>Customer</th>
                    <th>Job Loss Date</th>
                    <th>Last Employer</th>
                    <th>Documents</th>
                    <th>Status</th>
                    <th>Filed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.map((claim) => (
                    <tr key={claim.claim_id}>
                      <td className="font-mono text-sm">{claim.claim_id}</td>
                      <td>
                        <div>
                          <p className="font-medium">{claim.user?.full_name || "N/A"}</p>
                          <p className="text-sm text-muted-foreground">{claim.user?.email}</p>
                        </div>
                      </td>
                      <td>{new Date(claim.job_loss_date).toLocaleDateString()}</td>
                      <td>{claim.last_employer}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>{claim.documents?.length || 0}</span>
                        </div>
                      </td>
                      <td>
                        <Badge className={getStatusBadge(claim.status)}>
                          {claim.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {new Date(claim.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/agent/claims/${claim.claim_id}`)}
                            data-testid={`view-claim-${claim.claim_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {claim.status !== "paid" && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteClick(claim)}
                              data-testid={`delete-claim-${claim.claim_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No claims found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Claim</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this claim? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {claimToDelete && (
            <div className="py-4 space-y-2">
              <p><strong>Claim ID:</strong> {claimToDelete.claim_id}</p>
              <p><strong>Customer:</strong> {claimToDelete.user?.full_name || "N/A"}</p>
              <p><strong>Last Employer:</strong> {claimToDelete.last_employer}</p>
              <p><strong>Status:</strong> {claimToDelete.status}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              data-testid="confirm-delete-claim"
            >
              Delete Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
